<template>

    <div class="container">

        <app-head title="Suppliers" />

        <div class="d-flex justify-content-start align-items-center mb-3 gap-3">
            <h1 class="">{{ __('Suppliers') }}</h1>
            <div class="">
                <inertia-link class="btn btn-primary" :href="route('admin.suppliers.create')">
                    {{ __('Create') }}
                </inertia-link>
            </div>
        </div>

        <div class="d-flex mb-3">
            <search-filter v-model="form.search" class="" @reset="reset">
                <select-input label="Status" v-model="form.status">
                    <option :value="null">{{ __('Veröffentlicht') }}</option>
                    <option value="trashed">{{ __('Papierkorb') }}</option>
                </select-input>
                <select-input label="supplier_group_id" v-model="form.supplier_group_id">
                    <option :value="null">{{ __('All') }}</option>
                    <option :value="supplierGroup.id" v-for="supplierGroup in supplierGroups">{{ supplierGroup.name }}</option>
                </select-input>
            </search-filter>
        </div>

        <div class="d-flex justify-content-between">
            <results :meta="suppliers.meta" />
            <div>
                <i class="bi bi-download"></i>
                {{ __('Export') }}
                <a :href="getExportUrl('xlsx')">
                    {{ __('XLSX') }}
                </a>
                /
                <a :href="getExportUrl('csv')">
                    {{ __('CSV') }}
                </a>
                /
                <a :href="getExportUrl('ods')">
                    {{ __('ODS') }}
                </a>
            </div>
        </div>

        <div class="card">
            <data-table :items="suppliers.data"
                        :links="suppliers.meta.links"
                        :columns="columns"
                        >
                <data-table-row v-for="supplier in suppliers.data" :key="supplier.id">
                    <td>
                        {{ supplier.id }}
                    </td>
                    <td>

                        <div v-if="supplier.deleted_at">
                            {{ supplier.name }}
                        </div>
                        <template v-else>
                            <inertia-link class="fw-bold" :href="route('admin.suppliers.edit', supplier)">
                                <div class="mb-2" v-if="supplier.logo">
                                    <img class="" :src="supplier.logo" alt="" style="max-height: 20px; width: auto;" />
                                </div>
                                <div>
                                    {{ supplier.short_name }}
                                </div>
                            </inertia-link>
                            <div class="small">
                                {{ supplier.name }}
                            </div>
                        </template>

                    </td>
                    <td>
                        <boolean :value="supplier.enabled" />
                    </td>
                    <td>
                        {{ supplier.email || '-' }}
                    </td>
                    <td>
                        <div v-if="supplier.supplier_group">
                            {{ supplier.supplier_group.name }}
                        </div>
                        <div v-else>
                            -
                        </div>
                    </td>
                    <td>
                        <inertia-link class="" :href="route('admin.products.index', {supplier_id: supplier.id})">
                            {{ supplier.number_products || 0 | localizeNumber }}
                        </inertia-link>
                    </td>
                    <!--                    <td>
                                            <inertia-link class="" :href="route('admin.suppliers.properties.index', {supplier})">
                                                {{ supplier.supplier_properties_count || 0 | localizeNumber }}
                                            </inertia-link>
                                        </td>-->
                    <td>
                        <inertia-link class="" :href="route('admin.attachments.index', {supplier_id: supplier.id})">
                            {{ supplier.attachments_count || 0 | localizeNumber }}
                        </inertia-link>
                    </td>
                    <td>
                        {{ supplier.revenue || 0 | localizeAmount }}
                    </td>
                    <td>
                        {{ supplier.scoring || '-' }}
                    </td>
                    <td class="text-end py-0">

                        <template v-if="supplier.deleted_at">
                            <button type="button" class="btn btn-outline-success" @click="restore(supplier)">
                                {{ __('Restore') }}
                            </button>
                            <button type="button" class="btn btn-outline-danger" @click="destroyPermanently(supplier)">
                                {{ __('Destroy permanently') }}
                            </button>
                        </template>

                        <div class="dropdown" v-else>
                            <button class="btn btn-outline-secondary" 
                                    type="button" 
                                    :id="`dropdownMenuSupplier-${supplier.id}`" 
                                    :title="__('Edit')"
                                    data-bs-toggle="dropdown" 
                                    aria-expanded="false">
                                <i class="bi bi-three-dots-vertical"></i>
                                {{ __('Edit') }}
                            </button>
                            <div class="dropdown-menu dropdown-menu-end" :aria-labelledby="`dropdownMenuSupplier-${supplier.id}`">
                                <inertia-link :href="route('admin.suppliers.edit', {supplier})" class="dropdown-item">
                                    {{ __('Bearbeiten') }}
                                </inertia-link>
                                <inertia-link :href="route('admin.analytics.index', {supplier_id: supplier.id, origin: route('admin.suppliers.index')})" class="dropdown-item">
                                    {{ __('Analytics') }}
                                </inertia-link>
                                <inertia-link :href="route('admin.users.index', {account_id: supplier.id})" class="dropdown-item">
                                    {{ __('Users') }}
                                </inertia-link>
                                <inertia-link :href="route('admin.discounts.index', {supplier_id: supplier.id})" class="dropdown-item">
                                    {{ __('Discounts') }}
                                </inertia-link>
                                <inertia-link :href="route('admin.suppliers.categories.index', supplier)" class="dropdown-item">
                                    {{ __('Categories') }}
                                </inertia-link>
                                <inertia-link :href="route('admin.suppliers.properties.index', supplier)" class="dropdown-item">
                                    {{ __('Properties') }}
                                </inertia-link>
                                <inertia-link :href="route('admin.suppliers.delivery-options.index', {supplier})" class="dropdown-item">
                                    {{ __('Delivery Options') }}
                                </inertia-link>
                                <inertia-link :href="route('admin.suppliers.payment-methods.index', {supplier})" class="dropdown-item">
                                    {{ __('Payment Methods') }}
                                </inertia-link>
                                <inertia-link :href="route('admin.suppliers.customer-relations.index', {supplier})" class="dropdown-item">
                                    {{ __('Customer Relations') }}
                                </inertia-link>
                            </div>
                        </div>
                    </td>
                </data-table-row>
            </data-table>
        </div>

    </div>

</template>

<script>

    import DataTable from '@/shared/Admin/DataTable';
    import DataTableRow from '@/shared/Admin/DataTableRow';
    import SearchFilter from '@/shared/Admin/SearchFilter';

    import SelectInput from '@/shared/Inputs/SelectInput';
    import Results from '@/shared/Admin/Results';
    import Boolean from '@/shared/Admin/Boolean';

    export default {
        props: [
            'filters',
            'suppliers',
            'supplierGroups',
        ],
        components: {
            DataTable,
            DataTableRow,
            SearchFilter,
            SelectInput,
            Results,
            Boolean,
        },
        created() {
        },
        data() {
            return {
                form: {
                    search: this.filters.search,
                    status: this.filters.status,
                    supplier_group_id: this.filters.supplier_group_id,
                },
                columns: {
                    id: {label: 'ID', sortable: 'id'},
                    short_name: {label: 'Name', sortable: 'short_name'},
                    enabled: {label: 'Enabled', sortable: 'enabled'},
                    email: {label: 'Email', sortable: 'email'},
                    supplier_group: {label: 'Supplier Group'},
                    number_products: {label: 'Products', sortable: 'number_products'},
//                    properties: {label: 'Properties'},
                    attachments: {label: 'Attachments'},
                    revenue: {label: 'Revenue', sortable: 'revenue'},
                    scoring: {label: 'Scoring', sortable: 'scoring'}, // @todo nur visible für admin
                },
            }
        },
        watch: {
            form: {
                deep: true,
                handler: _.debounce(function () {
                    this.$inertia.get(this.route('admin.suppliers.index'), this.form, {preserveState: true})
                }, 300, {trailing: true}),
            },
        },
        methods: {
            reset() {
                this.form = _.mapValues(this.form, () => null)
            },
            destroyPermanently(supplier) {
                if (confirm(this.__('Sind Sie sicher?'))) {
                    this.$inertia.delete(this.route('admin.suppliers.destroy-permanently', supplier.id));
                }
            },
            restore(supplier) {
                this.$inertia.get(this.route('admin.suppliers.restore', supplier.id));
            },
            getExportUrl(format) {
                return this.route('admin.suppliers.export', {format: format, ...this.filters});
            }
        },
    }
</script>